import { useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import { Button } from '@rekrytering/ui/components/button'
import { Input } from '@rekrytering/ui/components/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@rekrytering/ui/components/select'
import { Textarea } from '@rekrytering/ui/components/textarea'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

type Topic =
  | 'bug'
  | 'technical_issue'
  | 'billing'
  | 'general'
  | 'feature_request'
type Severity = 'low' | 'medium' | 'high' | 'critical'

export function ContactSupportForm() {
  const { t } = useTranslation()

  const [topic, setTopic] = useState<Topic>('general')
  const [severity, setSeverity] = useState<Severity>('low')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [submittedTicketId, setSubmittedTicketId] = useState<string | null>(
    null,
  )

  const { mutateAsync: createTicket, isPending } = useMutation({
    mutationFn: useConvexMutation(
      api.features.support.recruiter_ticket.createMyTicket,
    ),
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const ticketId = await createTicket({ topic, severity, subject, message })
    setSubmittedTicketId(ticketId)
  }

  const canSubmit = subject.trim().length > 0 && message.trim().length > 0

  return (
    <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
      <div className="flex w-full flex-col gap-2 p-6">
        <h2 className="text-xl font-medium text-primary">
          {t('settings.contactSupport.title')}
        </h2>
        <p className="text-sm font-normal text-primary/60">
          {t('settings.contactSupport.description')}
        </p>
      </div>

      {submittedTicketId ? (
        <div className="flex w-full flex-col gap-2 border-t border-border px-6 py-5">
          <p className="text-sm font-medium text-primary">
            {t('settings.contactSupport.successTitle')}
          </p>
          <p className="text-sm text-primary/60">
            {t('settings.contactSupport.successMessage', {
              ticketId: submittedTicketId,
            })}
          </p>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="flex w-full flex-col gap-4 border-t border-border px-6 py-5"
        >
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium">
                {t('settings.contactSupport.topicLabel')}
              </label>
              <Select value={topic} onValueChange={(v) => setTopic(v as Topic)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bug">
                    {t('settings.contactSupport.topicBug')}
                  </SelectItem>
                  <SelectItem value="technical_issue">
                    {t('settings.contactSupport.topicTechnicalIssue')}
                  </SelectItem>
                  <SelectItem value="billing">
                    {t('settings.contactSupport.topicBilling')}
                  </SelectItem>
                  <SelectItem value="general">
                    {t('settings.contactSupport.topicGeneral')}
                  </SelectItem>
                  <SelectItem value="feature_request">
                    {t('settings.contactSupport.topicFeatureRequest')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium">
                {t('settings.contactSupport.severityLabel')}
              </label>
              <Select
                value={severity}
                onValueChange={(v) => setSeverity(v as Severity)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">
                    {t('settings.contactSupport.severityLow')}
                  </SelectItem>
                  <SelectItem value="medium">
                    {t('settings.contactSupport.severityMedium')}
                  </SelectItem>
                  <SelectItem value="high">
                    {t('settings.contactSupport.severityHigh')}
                  </SelectItem>
                  <SelectItem value="critical">
                    {t('settings.contactSupport.severityCritical')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium">
              {t('settings.contactSupport.subjectLabel')}
            </label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t('settings.contactSupport.subjectPlaceholder')}
              maxLength={200}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium">
              {t('settings.contactSupport.messageLabel')}
            </label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('settings.contactSupport.messagePlaceholder')}
              rows={5}
              maxLength={2000}
              className="resize-none"
            />
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={!canSubmit || isPending}>
              {isPending
                ? t('settings.contactSupport.submitting')
                : t('settings.contactSupport.submit')}
            </Button>
          </div>
        </form>
      )}

      <div className="flex min-h-14 w-full items-center rounded-lg rounded-t-none border-t border-border bg-secondary px-6 dark:bg-card">
        <p className="text-sm font-normal text-primary/60">
          {t('settings.contactSupport.hint')}
        </p>
      </div>
    </div>
  )
}
